/**
 * MerchantBannerOptimization — the merchant-grade view of an always-on banner slot
 * (OPTIMIZATIONS-SPEC.md §5).
 *
 * The A/B results screen answers "¿cuál ganó?" once. This one answers "¿qué estamos mostrando hoy,
 * y sigue funcionando?" forever, so its shape is different: share bars instead of a verdict, and a
 * freshness alert that can appear years after launch.
 *
 * Language rules are the merchant tier's (MERCHANT-EXECUTION-BRIEF): rates are always "N de cada
 * 100", never a percentage, never CTR or lift. Share of traffic IS shown as a percentage — that one
 * is an instruction we're carrying out ("lo mostramos 7 de cada 10 veces"), not a statistic.
 *
 * Green is reserved for the one good-news state (a clear winner emerged). Fatigue is orange, never
 * red: a banner wearing out is normal and the merchant did nothing wrong.
 */

import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { Card, Text, Tag, Button, Spinner } from '@nimbus-ds/components'
import { experimentService } from '@/api/experiments'
import { trackAppEvent, type AppTelemetryEvent } from '@/lib/appTelemetry'
import { useSetMerchantChrome } from '@/components/layout/MerchantChrome'
import { fmtInt } from '@/lib/formatEsAr'
import type { Experiment } from '@/types'

/** A slot is any self-serve banner row in optimization mode (the marker can sit on any arm). */
export function isBannerSlot(exp: Experiment): boolean {
  if (exp.mode !== 'optimization') return false
  return Object.values((exp.variants as any) || {}).some(
    (v: any) => v?.config?.self_serve_banner
  )
}

export type SlotState =
  | 'no_data'
  | 'learning'
  | 'allocating'
  | 'converged'
  | 'pinned'
  | 'single_arm'
  | 'control_changed'
  | 'stopped'

function deriveState(exp: Experiment): SlotState {
  if (exp.status === 'paused' || exp.status === 'completed') return 'stopped'
  if (exp.optimization?.control_drift_at) return 'control_changed'
  const alloc = exp.allocation
  if (!alloc) return 'no_data'
  if (alloc.reason === 'pinned') return 'pinned'
  if (alloc.reason === 'single_arm') return 'single_arm'
  if (alloc.reason === 'no_data') return 'no_data'
  if (alloc.reason === 'learning') return 'learning'
  return alloc.converged?.confirmed ? 'converged' : 'allocating'
}

const dayMs = 86_400_000
const startOf = (exp: Experiment) => new Date(exp.created_at || Date.now()).getTime()
const daysRunning = (exp: Experiment) =>
  Math.max(1, Math.ceil((Date.now() - startOf(exp)) / dayMs))

/** Clicks per 100 visits: whole at ≥10, one decimal below. Never a percentage. */
const per100 = (rate: number) => {
  const v = (rate || 0) * 100
  return v >= 10 ? String(Math.round(v)) : (Math.round(v * 10) / 10).toFixed(1)
}

const fmtDate = (ms: number) =>
  new Date(ms).toLocaleDateString('es-AR', { day: 'numeric', month: 'long' })

/** "Banner 2" for variant_b, and the merchant's own banner for control. */
function armLabel(exp: Experiment, arm: string): string {
  if (arm === 'control') return 'Tu banner de siempre'
  const keys = Object.keys(exp.variants || {}).filter((k) => k !== 'control').sort()
  const idx = keys.indexOf(arm)
  return `Banner ${idx + 2}`
}

/** First promoted image for an arm (swap or insert), so each row shows its own creative. */
function armImage(exp: Experiment, arm: string): string | null {
  if (arm === 'control') {
    const marker = Object.values((exp.variants as any) || {})
      .map((v: any) => v?.config?.self_serve_banner)
      .find(Boolean)
    return marker?.devices?.desktop?.control_image || marker?.control_image || null
  }
  const cfg = (exp.variants as any)?.[arm]?.config || {}
  for (const s of cfg.elements_to_swap || []) {
    if (s.image_url_desktop) return s.image_url_desktop
    if (s.image_url_mobile) return s.image_url_mobile
  }
  for (const i of cfg.elements_to_insert || []) {
    if (i.image_url) return i.image_url
  }
  return null
}

function controlAspect(exp: Experiment): string {
  const marker = Object.values((exp.variants as any) || {})
    .map((v: any) => v?.config?.self_serve_banner)
    .find(Boolean)
  const size = marker?.devices?.desktop?.control_size || marker?.control_size
  return size?.width && size?.height ? `${size.width} / ${size.height}` : '16 / 5'
}

// ---------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------

const HERO: Record<SlotState, { emoji: string; title: string; body: string }> = {
  no_data: {
    emoji: '🌱',
    title: 'Todavía no hay visitas suficientes',
    body: 'Apenas empiecen a entrar visitas vas a ver acá cuál banner se lleva más clics.',
  },
  learning: {
    emoji: '🔎',
    title: 'Estamos aprendiendo',
    body: 'Por ahora mostramos todos por igual. En cuanto veamos una diferencia, empezamos a mostrar más seguido el que mejor funciona.',
  },
  allocating: {
    emoji: '🎛️',
    title: 'Ajustándose solo',
    body: 'Ya estamos mostrando más seguido el que mejor viene funcionando. Se recalcula todos los días.',
  },
  converged: {
    emoji: '🏆',
    title: 'Ya sabemos cuál funciona mejor',
    body: 'Lo mostramos casi siempre. Si querés, podés dejarlo fijo — y si algún día se gasta, te avisamos igual.',
  },
  pinned: {
    emoji: '📌',
    title: 'Estás mostrando siempre el mismo banner',
    body: 'Lo dejaste fijo, así que no rotamos. Seguimos mirando si empieza a perder interés.',
  },
  single_arm: {
    emoji: '☝️',
    title: 'Quedó un solo banner en rotación',
    body: 'Sin al menos dos no hay nada que optimizar. Sumá otro para que vuelva a ajustarse solo.',
  },
  control_changed: {
    emoji: '🔄',
    title: 'Cambiaste tu banner',
    body: 'Lo empezamos a medir de nuevo desde hoy. Confirmanos cuál es ahora para que podamos seguir cuidándolo.',
  },
  stopped: {
    emoji: '⏸️',
    title: 'Optimización detenida',
    body: 'Tu tienda volvió a mostrar solo tu banner de siempre. Lo medido hasta acá quedó guardado.',
  },
}

const HERO_SURFACE: Record<SlotState, string> = {
  no_data: 'bg-transparent',
  learning: 'bg-transparent',
  allocating: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  converged: 'rounded-lg border border-[color-mix(in_srgb,#00935b_25%,#eaf7f0)] bg-[#eaf7f0] p-4',
  pinned: 'rounded-lg border border-[color-mix(in_srgb,#0059d5_25%,#eef5ff)] bg-[#eef5ff] p-4',
  single_arm: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  control_changed: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
  stopped: 'rounded-lg border border-[#e7e7e7] bg-white p-4',
}

function Hero({ state }: { state: SlotState }) {
  const h = HERO[state]
  return (
    <div className={`flex items-start gap-3 ${HERO_SURFACE[state]}`}>
      <span className="text-[28px] leading-none" aria-hidden="true">{h.emoji}</span>
      <div className="flex min-w-0 flex-col gap-1">
        <span className="text-lg font-bold leading-tight text-[var(--nimbus-colors-neutral-textHigh)]">
          {h.title}
        </span>
        <Text>{h.body}</Text>
      </div>
    </div>
  )
}

/**
 * The freshness alert. Deliberately orange rather than red, and it must read well with NO cause
 * hint — on a store this size that is the common case, not an edge case (§3).
 */
function FatigueCard({ exp }: { exp: Experiment }) {
  const f = exp.fatigue
  if (!f || f.status !== 'flagged') return null
  const cause =
    f.cause_hint === 'creative'
      ? 'Los demás banners mantuvieron su ritmo, así que parece ser la imagen y no la época.'
      : f.cause_hint === 'traffic'
        ? 'Los demás banners bajaron parecido, así que puede ser la época del año o de dónde viene tu tráfico.'
        : 'Todavía no podemos decirte por qué — con más visitas vamos a poder.'
  return (
    <div className="rounded-lg border border-[color-mix(in_srgb,#c8791a_30%,#fdf3e7)] bg-[#fdf3e7] p-4">
      <div className="flex items-start gap-3">
        <span className="text-[24px] leading-none" aria-hidden="true">📉</span>
        <div className="flex flex-col gap-2">
          <span className="font-bold text-[var(--nimbus-colors-neutral-textHigh)]">
            {armLabel(exp, f.arm)} está perdiendo interés
          </span>
          <Text>
            Antes lo clickeaban {per100(f.baseline.rate)} de cada 100 visitas; esta última semana,{' '}
            {per100(f.recent.rate)} de cada 100. {cause}
          </Text>
          <Link to="/merchant/banner-optimization" className="no-underline">
            <Button appearance="primary">Sumá un banner nuevo</Button>
          </Link>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Arm rows
// ---------------------------------------------------------------------------

function ArmRow({
  exp,
  arm,
  share,
  leader,
  retired,
  aspect,
  onRetire,
  onPin,
  canAct,
}: {
  exp: Experiment
  arm: string
  share: number
  leader: boolean
  retired: boolean
  aspect: string
  onRetire: () => void
  onPin: () => void
  canAct: boolean
}) {
  const src = armImage(exp, arm)
  const cell = exp.allocation?.arms?.[arm]
  const rate = cell?.rate || 0
  const visits = cell?.n_raw || 0

  return (
    <div className={`flex items-center gap-3 ${retired ? 'opacity-55' : ''}`}>
      {src ? (
        <img
          src={src}
          alt={armLabel(exp, arm)}
          className="w-24 shrink-0 rounded-md border border-[#e7e7e7] object-cover"
          style={{ aspectRatio: aspect }}
        />
      ) : (
        <div
          className="w-24 shrink-0 rounded-md border border-dashed border-[#d4d4d4] bg-[#fafafa]"
          style={{ aspectRatio: aspect }}
        />
      )}
      <div className="flex min-w-0 grow flex-col gap-1">
        <div className="flex flex-wrap items-center gap-2">
          <Text fontWeight="medium" color="neutral-textHigh">{armLabel(exp, arm)}</Text>
          {retired && <Tag appearance="neutral">Fuera de rotación</Tag>}
          {!retired && leader && <Tag appearance="success">El que más funciona</Tag>}
        </div>
        <Text fontSize="caption">
          {visits > 0
            ? `${per100(rate)} de cada 100 hicieron clic · ${fmtInt(visits)} visitas`
            : 'Todavía sin visitas suficientes'}
        </Text>
        {/* Share of traffic is an instruction we're executing, not a statistic — so it's the one
            number on this screen allowed to be a percentage. */}
        {!retired && (
          <div className="flex items-center gap-2">
            <div className="h-1.5 grow overflow-hidden rounded-full bg-[#ededed]">
              <div
                className="h-full rounded-full bg-[#0059d5]"
                style={{ width: `${Math.max(2, share)}%` }}
              />
            </div>
            <Text fontSize="caption">{share}% de las visitas</Text>
          </div>
        )}
      </div>
      {canAct && !retired && (
        <div className="flex shrink-0 flex-col gap-1">
          <button className="text-xs text-[#0059d5] hover:text-[#00429f]" onClick={onPin}>
            Dejar fijo
          </button>
          <button className="text-xs text-[#6a6a6a] hover:text-[#333]" onClick={onRetire}>
            Sacar
          </button>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------

export function MerchantBannerOptimization() {
  const navigate = useNavigate()
  const [exp, setExp] = useState<Experiment | null>(null)
  const [state, setState] = useState<SlotState>('no_data')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmStop, setConfirmStop] = useState(false)

  useSetMerchantChrome({
    title: 'Tu banner optimizado',
    subtitle: exp ? `Activa desde el ${fmtDate(startOf(exp))} · ${daysRunning(exp)} días` : undefined,
    backTo: '/merchant',
    showStoreOnDesktop: true,
  })

  const load = async () => {
    try {
      const all = await experimentService.getExperiments()
      const latest = all
        .filter(isBannerSlot)
        .sort((a, b) => (b.created_at || '').localeCompare(a.created_at || ''))[0] || null
      setExp(latest)
      if (latest) setState(deriveState(latest))
    } catch {
      setError('No pudimos cargar tu optimización. Probá de nuevo en un rato.')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    trackAppEvent('optimization_viewed')
    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const act = async (fn: () => Promise<unknown>, event: AppTelemetryEvent) => {
    if (busy) return
    setBusy(true)
    try {
      await fn()
      trackAppEvent(event)
      await load()
    } catch {
      setError('No pudimos guardar el cambio. Probá de nuevo en un rato.')
    } finally {
      setBusy(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-10">
        <Spinner size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <Card>
        <Card.Body><Text>{error}</Text></Card.Body>
      </Card>
    )
  }

  if (!exp) {
    return (
      <div className="flex flex-col items-center gap-4 py-10 text-center">
        <span className="text-[44px] leading-none">🎛️</span>
        <Text>Todavía no tenés una optimización activa.</Text>
        <Link to="/merchant/banner-optimization" className="no-underline">
          <Button appearance="primary">Optimizá tu banner</Button>
        </Link>
      </div>
    )
  }

  const retired = exp.optimization?.retired_arms || []
  const pinned = exp.optimization?.pinned_arm || null
  const weights = exp.traffic_split || {}
  const leader = exp.allocation?.leader || null
  // Control first, then the challengers in their stable order — never by rank, so the list doesn't
  // reshuffle under the merchant every time the allocation moves.
  const armKeys = Object.keys(exp.variants || {}).sort((a, b) =>
    a === 'control' ? -1 : b === 'control' ? 1 : a.localeCompare(b)
  )
  const aspect = controlAspect(exp)
  const canAct = state !== 'stopped' && !busy

  return (
    <div className="flex flex-col gap-5">
      <Hero state={state} />
      <FatigueCard exp={exp} />

      {state === 'control_changed' && (
        <Card>
          <Card.Body>
            <div className="flex flex-col gap-2">
              <Text>
                Mientras tanto seguimos rotando normalmente — solo perdemos de vista si volvés a
                cambiarlo.
              </Text>
              <Button
                appearance="primary"
                disabled={busy}
                onClick={() => act(() => experimentService.reconfirmOptimizationControl(exp.id), 'optimization_reconfirmed')}
              >
                Ya lo confirmé
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Card>
        <Card.Header title="Tus banners" />
        <Card.Body>
          <div className="flex flex-col gap-4">
            {armKeys.map((arm) => (
              <ArmRow
                key={arm}
                exp={exp}
                arm={arm}
                share={Number(weights[arm]) || 0}
                leader={arm === leader && !pinned}
                retired={retired.includes(arm)}
                aspect={aspect}
                canAct={canAct && armKeys.filter((k) => !retired.includes(k)).length > 1}
                onPin={() => act(() => experimentService.pinOptimizationArm(exp.id, arm), 'optimization_pinned')}
                onRetire={() =>
                  act(() => experimentService.retireOptimizationArms(exp.id, [...retired, arm]), 'optimization_retired')
                }
              />
            ))}
          </div>
        </Card.Body>
      </Card>

      {pinned && (
        <Card>
          <Card.Body>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <Text>Estás mostrando siempre {armLabel(exp, pinned)}.</Text>
              <Button
                disabled={busy}
                onClick={() => act(() => experimentService.unpinOptimization(exp.id), 'optimization_unpinned')}
              >
                Volver a rotar
              </Button>
            </div>
          </Card.Body>
        </Card>
      )}

      <Text fontSize="caption">
        Todos los días revisamos cuál se lleva más clics y ajustamos cuánto se muestra cada uno.
        Seguimos mostrando los otros cada tanto para darnos cuenta si el que va ganando se gasta.
      </Text>

      {state !== 'stopped' && (
        <Card>
          <Card.Body>
            {confirmStop ? (
              <div className="flex flex-col gap-3">
                <Text fontWeight="medium" color="neutral-textHigh">¿Frenar la optimización?</Text>
                <Text>
                  Tu tienda vuelve a mostrar solo tu banner de siempre. Lo medido hasta acá queda
                  guardado.
                </Text>
                <div className="flex flex-wrap gap-2">
                  <Button
                    appearance="danger"
                    disabled={busy}
                    onClick={() =>
                      act(async () => {
                        await experimentService.pauseExperiment(exp.id)
                        navigate('/merchant')
                      }, 'optimization_stopped')
                    }
                  >
                    Sí, frenarla
                  </Button>
                  <Button appearance="transparent" onClick={() => setConfirmStop(false)}>
                    Dejarla andando
                  </Button>
                </div>
              </div>
            ) : (
              <button
                className="text-xs text-[#6a6a6a] hover:text-[#333]"
                onClick={() => setConfirmStop(true)}
              >
                Frenar la optimización
              </button>
            )}
          </Card.Body>
        </Card>
      )}
    </div>
  )
}

export default MerchantBannerOptimization
